import type { ReactNode } from 'react';
import { CUSTOM_COURT_IMG_PLACEHOLDER_SRC } from 'constants/media';
import Court from 'svg/Court';
import Directions from 'svg/Directions';
import Location from 'svg/Location';
import Net from 'svg/Net';
import Users from 'svg/Users';
import type { FindVenueDetails } from 'components/Find/types';
import Link from 'components/Link';
import classNames from 'styles/utils/classNames';

interface Props {
  venue: FindVenueDetails;
  /** `lg`: Figma's wider "Modal M" card from lg up: a fixed 180px photo and larger type. */
  sizeDesktop?: 'lg';
}

const directionsUrl = (address: string) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;

/** One "icon + label" fact in the venue card's wrapped meta row. */
function VenueMeta({
  icon,
  label,
  isLargeAtDesktop,
}: {
  icon: ReactNode;
  label: string;
  isLargeAtDesktop: boolean;
}) {
  return (
    <span className={classNames('flex items-center gap-0.5', isLargeAtDesktop && 'lg:gap-ds-xs')}>
      <span className="shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon">
        {icon}
      </span>
      <span
        className={classNames(
          'typography-product-text-card-mobile text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
          isLargeAtDesktop && 'lg:typography-product-text-card-desktop',
        )}
      >
        {label}
      </span>
    </span>
  );
}

function Divider() {
  return (
    <div className="h-px w-full bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
  );
}

/** Horizontal venue card + address + Directions link, wrapped in dividers. Shared
 *  by the game's Location details and a player's Preferred locations. */
export default function VenueDetailCard({ venue, sizeDesktop }: Props) {
  const isLargeAtDesktop = sizeDesktop === 'lg';
  const metaIconClass = classNames('h-3 w-3', isLargeAtDesktop && 'lg:h-4 lg:w-4');

  return (
    <div className="flex flex-col gap-ds-lg">
      <div
        className={classNames('flex items-start gap-ds-md', isLargeAtDesktop && 'lg:items-center')}
      >
        <div
          className={classNames(
            'h-[6.25rem] min-w-0 flex-1 overflow-hidden rounded-lg bg-color-bg-lightmode-secondary dark:bg-color-bg-darkmode-secondary',
            isLargeAtDesktop && 'lg:h-28 lg:w-[11.25rem] lg:flex-none',
          )}
        >
          <img
            src={venue.imageUrl || CUSTOM_COURT_IMG_PLACEHOLDER_SRC}
            alt={venue.name}
            className={
              venue.imageUrl ? 'h-full w-full object-cover' : 'h-full w-full object-contain p-ds-md'
            }
          />
        </div>

        <div
          className={classNames(
            'flex min-w-0 flex-1 flex-col gap-ds-xs',
            isLargeAtDesktop && 'lg:gap-ds-sm',
          )}
        >
          <div
            className={classNames('flex items-center gap-0.5', isLargeAtDesktop && 'lg:gap-ds-xs')}
          >
            <Location
              className={classNames(
                'h-3 w-3 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon',
                isLargeAtDesktop && 'lg:h-4 lg:w-4',
              )}
            />
            <span
              className={classNames(
                'typography-product-text-card-mobile truncate text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
                isLargeAtDesktop && 'lg:typography-product-text-card-desktop',
              )}
            >
              {venue.locationLabel}
            </span>
          </div>

          <h4
            className={classNames(
              'typography-product-card-title-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
              isLargeAtDesktop && 'lg:typography-product-card-title-desktop',
            )}
          >
            {venue.name}
          </h4>

          {/* A typed-in address has no venue facts; each item hides when it has none. */}
          {(venue.accessLabel || venue.courtCount > 0 || venue.netLabel) && (
            <div className="flex flex-wrap gap-x-ds-md gap-y-ds-xs">
              {venue.accessLabel && (
                <VenueMeta
                  icon={<Users className={metaIconClass} />}
                  label={venue.accessLabel}
                  isLargeAtDesktop={isLargeAtDesktop}
                />
              )}
              {venue.courtCount > 0 && (
                <VenueMeta
                  icon={<Court className={metaIconClass} />}
                  label={`${venue.courtCount} ${venue.courtCount === 1 ? 'Court' : 'Courts'}`}
                  isLargeAtDesktop={isLargeAtDesktop}
                />
              )}
              {venue.netLabel && (
                <VenueMeta
                  icon={<Net className={metaIconClass} />}
                  label={venue.netLabel}
                  isLargeAtDesktop={isLargeAtDesktop}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <Divider />

      <div className="flex items-center gap-ds-xl">
        <p className="typography-product-caption flex-1 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {venue.fullAddress}
        </p>

        <Link
          href={directionsUrl(venue.fullAddress)}
          isExternal
          className="typography-product-button-label-medium flex shrink-0 items-center gap-ds-xs text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
        >
          <Directions className="h-4 w-4" />
          Directions
        </Link>
      </div>

      <Divider />
    </div>
  );
}
